import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Link from "next/link";
import { motion } from "framer-motion";
import { credBadges } from "./homeData";
import { globalSpacing } from "../../src/theme";

export default function HeroSection() {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 80%)`,
      }}
      py={globalSpacing}
    >
      <Container sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Chip
                label="Designing future-ready web experiences"
                color="default"
                sx={{
                  alignSelf: { xs: "center", md: "flex-start" },
                  backgroundColor: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.5,
                  backdropFilter: "blur(6px)",
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.1,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                Building polished products that feel effortless to use.
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255,255,255,0.82)",
                  textAlign: { xs: "center", md: "left" },
                  maxWidth: 520,
                  mx: { xs: "auto", md: 0 },
                }}
              >
                I help teams design, prototype, and launch immersive digital experiences that convert users into advocates.
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  justifyContent: { xs: "center", md: "flex-start" },
                  alignItems: { xs: "stretch", sm: "center" },
                }}
              >
                <Button
                  component={Link}
                  href="#projects"
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "999px",
                    fontWeight: 600,
                    fontSize: "1rem",
                  }}
                >
                  Explore recent work
                </Button>
                <Button
                  component={Link}
                  href="/contact"
                  variant="outlined"
                  color="inherit"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "999px",
                    fontWeight: 600,
                    fontSize: "1rem",
                    borderColor: "rgba(255,255,255,0.6)",
                    color: "#fff",
                    '&:hover': {
                      borderColor: "#fff",
                      backgroundColor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  Let&apos;s collaborate
                </Button>
              </Stack>
              <Stack
                direction="row"
                spacing={1.5}
                flexWrap="wrap"
                sx={{
                  justifyContent: { xs: "center", md: "flex-start" },
                  color: "rgba(255,255,255,0.9)",
                  mt: 1,
                }}
              >
                {credBadges.map((badge) => {
                  const Icon = badge.icon;
                  return (
                    <Chip
                      key={badge.label}
                      icon={<Icon fontSize="small" />}
                      label={badge.label}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: "inherit",
                        borderRadius: "999px",
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: { xs: "88%", md: "100%" },
                  maxWidth: 440,
                  borderRadius: "36px",
                  p: { xs: 2.5, md: 3 },
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(15, 23, 42, 0.72)"
                      : "rgba(255,255,255,0.92)",
                  backdropFilter: "blur(14px)",
                  boxShadow:
                    "0 36px 72px rgba(15, 23, 42, 0.26), 0 8px 24px rgba(15, 23, 42, 0.14)",
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.35)" : "rgba(15, 23, 42, 0.08)"}`,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    borderRadius: "28px",
                    overflow: "hidden",
                    aspectRatio: "4 / 5",
                    backgroundImage:
                      "linear-gradient(160deg, rgba(15,23,42,0.45) 10%, rgba(37,99,235,0.15) 55%, rgba(34,211,238,0.35) 100%), url('/krushna_raut_profile.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35) 0%, transparent 55%)",
                      mixBlendMode: "screen",
                    }}
                  />
                  <Stack
                    spacing={1}
                    sx={{
                      position: "absolute",
                      left: 20,
                      bottom: 20,
                      right: 20,
                      p: 2,
                      borderRadius: "20px",
                      backgroundColor: "rgba(15, 23, 42, 0.55)",
                      backdropFilter: "blur(12px)",
                      color: "#f8fafc",
                    }}
                  >
                    <Typography variant="overline" sx={{ letterSpacing: 2 }}>
                      Meet Krushna Raut
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      Experienced Full-Stack Developer
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(248,250,252,0.8)" }}>
                      Crafting immersive product stories with resilient architecture, motion, and human empathy.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(circle at top, rgba(255,255,255,0.18), transparent 55%)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
